import React from 'react';
import { Scroll, Book, ArrowRight } from 'lucide-react';

interface VocabularyEntryProps {
    greek: string;
    english: string;
}

const VocabularyEntry: React.FC<VocabularyEntryProps> = ({ greek, english }) => (
    <div className="flex items-center py-2 border-b border-golden-yellow last:border-b-0 overflow-x-auto">
        <span className="text-lg font-semibold text-terracotta whitespace-nowrap">
            {greek}
        </span>
        <ArrowRight className="text-amber-500 mx-2 flex-shrink-0" size={16} />
        <span className="text-md text-amber-700 whitespace-nowrap">
            {english}
        </span>
    </div>
);

interface VocabularyCardProps {
    entries: VocabularyEntryProps[];
    letter: string;
}

const VocabularyCard: React.FC<VocabularyCardProps> = ({ entries, letter }) => (
    <div className="bg-amber-50 rounded-lg shadow-md p-4 border border-amber-200 
        w-full overflow-hidden">
        <div className="flex items-center mb-4">
            <Scroll className="text-amber-600 mr-2 flex-shrink-0" size={24} />
            <h2 className="text-2xl font-bold text-terracotta">{letter}</h2>
        </div>
        <div className="space-y-2 w-full">
            {entries.map((entry, index) => (
                <VocabularyEntry key={index} greek={entry.greek} english={entry.english} />
            ))}
        </div>
    </div>
);

const NemeanLion = () => {
    const allEntries = [
        {
            letter: 'Α',
            entries: [
                { greek: 'Άνθρωπος', english: 'person' },
                { greek: 'Λιοντάρι', english: 'lion' },
                { greek: 'Βουνό', english: 'mountain' },
                { greek: 'Ρόπαλο', english: 'club' },
            ]
        },
        {
            letter: 'Β',
            entries: [
                { greek: 'Βασιλιάς', english: 'king' },
                { greek: 'βέλος', english: 'arrow' },
                { greek: 'Σπηλιά', english: 'cave' },
                { greek: 'Πιθάρι', english: 'urn' },
            ]
        },
        {
            letter: 'Γ',
            entries: [
                { greek: 'Φυτό', english: 'plant' },
                { greek: 'Γίγαντας', english: 'giant' },
                { greek: 'Λιοντάρι', english: 'lion' },
                { greek: 'Πιθάρι', english: 'urn' },
            ]
        },
        {
            letter: 'Δ',
            entries: [
                { greek: 'Δέντρο', english: 'tree' },
                { greek: 'Βουνό', english: 'mountain' },
                { greek: 'Άνθρωπος', english: 'person' },
                { greek: 'Τόξο', english: 'arrow' },
            ]
        },
        {
            letter: 'Ε',
            entries: [
                { greek: 'Ευρυσθέας', english: 'Eurystheus' },
                { greek: 'Δέρμα', english: 'skin' },
                { greek: 'Λεοντάρι', english: 'lion' },
                { greek: 'Βουνό', english: 'mountain' },
            ]
        },
        {
            letter: 'Ζ',
            entries: [
                { greek: 'Ζώα', english: 'animals' },
                { greek: 'Άνθρωπος', english: 'person' },
                { greek: 'Σπίτια', english: 'houses' },
                { greek: 'Τόξο', english: 'arrow' },
            ]
        },
        {
            letter: 'Η',
            entries: [
                { greek: 'Ηρακλής', english: 'Hercules' },
                { greek: 'Βασιλιάς', english: 'king' },
                { greek: 'Φυτό', english: 'plant' },
                { greek: 'Λιοντάρι', english: 'lion' },
            ]
        },
        {
            letter: 'Θ',
            entries: [
                { greek: 'Λιοντάρι', english: 'lion' },
                { greek: 'Νεμέα', english: 'Nemea' },
                { greek: 'Θεός', english: 'God' },
                { greek: 'Βουνό', english: 'mountain' },
            ]
        },
        {
            letter: 'Ι',
            entries: [
                { greek: 'Ιστορία', english: 'history' },
                { greek: 'Ζώα', english: 'animals' },
                { greek: 'Δέρμα', english: 'skin' },
                { greek: 'Πιθάρι', english: 'urn' },
            ]
        },
        {
            letter: 'Κ',
            entries: [
                { greek: 'Κυνηγός', english: 'hunter' },
                { greek: 'Δέντρο', english: 'tree' },
                { greek: 'Άθλος', english: 'labor' },
                { greek: 'Θεός', english: 'God' },
            ]
        },
        {
            letter: 'Λ',
            entries: [
                { greek: 'Λιοντάρι', english: 'lion' },
                { greek: 'Λύκος', english: 'wolf' },
                { greek: 'Σπίτια', english: 'houses' },
                { greek: 'Ρόπαλο', english: 'club' },
            ]
        },
        {
            letter: 'Μ',
            entries: [
                { greek: 'Μυκήνες', english: 'Mycenae' },
                { greek: 'Θεός', english: 'God' },
                { greek: 'Δέντρο', english: 'tree' },
                { greek: 'Βασιλιάς', english: 'king' },
            ]
        },
        {
            letter: 'Ν',
            entries: [
                { greek: 'Νεμέα', english: 'Nemea' },
                { greek: 'Ηρακλής', english: 'Hercules' },
                { greek: 'Λιοντάρι', english: 'lion' },
                { greek: 'Τόξο', english: 'arrow' },
            ]
        },
        {
            letter: 'Ξ',
            entries: [
                { greek: 'Ξένος', english: 'foreigner' },
                { greek: 'Σπηλιά', english: 'cave' },
                { greek: 'Βουνό', english: 'mountain' },
                { greek: 'Φυτό', english: 'plant' },
            ]
        },
        {
            letter: 'Ο',
            entries: [
                { greek: 'Όνειρο', english: 'dream' },
                { greek: 'Ηρακλής', english: 'Hercules' },
                { greek: 'Βουνό', english: 'mountain' },
                { greek: 'Ζώα', english: 'animals' },
            ]
        },
        {
            letter: 'Π',
            entries: [
                { greek: 'Πελοπόννησος', english: 'Peloponnese' },
                { greek: 'Φυτό', english: 'plant' },
                { greek: 'Πόλη', english: 'city' },
                { greek: 'Άνθρωπος', english: 'person' },
            ]
        },
        {
            letter: 'Ρ',
            entries: [
                { greek: 'Ρόπαλο', english: 'club' },
                { greek: 'Δέντρο', english: 'tree' },
                { greek: 'Γίγαντας', english: 'giant' },
                { greek: 'Ευρυσθέας', english: 'Eurystheus' },
            ]
        },
        {
            letter: 'Σ',
            entries: [
                { greek: 'Σπηλιά', english: 'cave' },
                { greek: 'Τόξο', english: 'arrow' },
                { greek: 'Πιθάρι', english: 'urn' },
                { greek: 'Φυτό', english: 'plant' },
            ]
        },
        {
            letter: 'Τ',
            entries: [
                { greek: 'Τόπος', english: 'place' },
                { greek: 'Λιοντάρι', english: 'lion' },
                { greek: 'Δέρμα', english: 'skin' },
                { greek: 'Βουνό', english: 'mountain' },
            ]
        },
        {
            letter: 'Υ',
            entries: [
                { greek: 'Υψηλό', english: 'height' },
                { greek: 'Ηρακλής', english: 'Hercules' },
                { greek: 'Φυτό', english: 'plant' },
                { greek: 'Βασιλιάς', english: 'king' },
            ]
        },
        {
            letter: 'Φ',
            entries: [
                { greek: 'Φυτό', english: 'plant' },
                { greek: 'Χέρια', english: 'hands' },
                { greek: 'Βουνό', english: 'mountain' },
                { greek: 'Σπίτι', english: 'house' },
            ]
        },
        {
            letter: 'Χ',
            entries: [
                { greek: 'Χέρια', english: 'hands' },
                { greek: 'Πελοπόννησος', english: 'Peloponnese' },
                { greek: 'Όνειρο', english: 'dream' },
                { greek: 'Γίγαντας', english: 'giant' },
            ]
        },
        {
            letter: 'Ψ',
            entries: [
                { greek: 'Ψάρι', english: 'fish' },
                { greek: 'Μυκήνες', english: 'Mycenae' },
                { greek: 'Δέντρο', english: 'tree' },
                { greek: 'Άνθρωπος', english: 'person' },
            ]
        },
        {
            letter: 'Ω',
            entries: [
                { greek: 'Ώρα', english: 'time' },
                { greek: 'Θεός', english: 'God' },
                { greek: 'Σπίτια', english: 'houses' },
                { greek: 'Ρόπαλο', english: 'club' },
            ]
        },
    ];

    return (
        <div className="w-full max-w-6xl mx-auto p-4 overflow-x-hidden">
            <h1 className="text-3xl font-bold text-amber-800 mb-6 text-center flex justify-center items-center">
                <Book className="text-terracotta mr-2" size={32} />
                Nemean Lion Vocabulary
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allEntries.map((section, index) => (
                    <VocabularyCard key={index} entries={section.entries} letter={section.letter} />
                ))}
            </div>
        </div>
    );
};

export default NemeanLion;